import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './SignInMenu.css';

// Sign-in dropdown for the masthead.
//
// Shown to unauthenticated visitors. Clicking the button opens a small
// menu with three options:
//   - Resident sign in     → /login    (community-issued sign-in code)
//   - Individual sign in   → /signup   (email magic-link; same page
//                                      serves new subscribers AND
//                                      returning sign-ins, since
//                                      magic-link auth doesn't
//                                      distinguish the two)
//   - Administrator sign in → /admin/login
//
// Behavior:
//   - Click the button to toggle the menu
//   - Click outside the menu to close it
//   - Press Escape to close it
//   - Tab navigation is preserved within the menu (no focus trap, just
//     normal browser tab order through the links)
//
// We deliberately keep this simple — no animations, no fancy positioning.
// Three links in a small floating panel.
export default function SignInMenu() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close when clicking outside the menu's container
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <div className="signin-menu" ref={containerRef}>
      <button
        type="button"
        className="nav-link nav-link-action signin-menu-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Sign in
        <span className="signin-menu-caret" aria-hidden="true">
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open ? (
        <div className="signin-menu-panel" role="menu">
          <Link
            to="/login"
            className="signin-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <span className="signin-menu-item-label">Resident sign in</span>
            <span className="signin-menu-item-meta">
              Enter your sign-in code
            </span>
          </Link>
          <Link
            to="/signup"
            className="signin-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <span className="signin-menu-item-label">Individual sign in</span>
            <span className="signin-menu-item-meta">
              Sign in with your email
            </span>
          </Link>
          <Link
            to="/admin/login"
            className="signin-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <span className="signin-menu-item-label">Administrator sign in</span>
            <span className="signin-menu-item-meta">
              Activity directors and Quiltt staff
            </span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}
